import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import BadgeTradeDirection from 'src/components/Badge/BadgeTradeDirection';
import BadgeTradePointType from 'src/components/Badge/BadgeTradePointType';
import BadgeTradeRuleState from 'src/components/Badge/BadgeTradeRuleState';
import BadgeTradeType from 'src/components/Badge/BadgeTradeType';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import { useTradeRuleCounterParty } from 'src/hooks/useTradeRuleCounterParty';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import buildTimeRangeSummary from 'src/util/i18n/timeRange';
import { getMemberNodes } from 'src/util/mainDataBuilder';
import { hasSharedConditions as hasSharedTimeOfUseConditions } from 'src/util/timeOfUse';

import AdminMeterTradeRulesTableItemClause from '../AdminMeterTradeRulesTableItemClause';

const AdminMeterTradeRulesItemFragment = graphql`
  fragment AdminMeterTradeRulesItem_rule on TradeRule {
    uuid
    buyer {
      userId
      user { uuid email givenName familyName }
      community { uuid title }
      residualId
      tradePoint { uuid type }
    }
    seller {
      userId
      user { uuid email givenName familyName }
      community { uuid title }
      residualId
      tradePoint { uuid type }
    }
    clauses {
      edges {
        node {
          ...AdminMeterTradeRulesTableItemClause_clause
        }
      }
    }
    tradeType
    state
    start
    finish
    proposedAt
    proposedBy { uuid email givenName familyName }
    acceptedAt
    acceptedBy { uuid email givenName familyName }
    rejectedAt
    rejectedBy { uuid email givenName familyName }
    cancelledAt
    cancelledBy { uuid email givenName familyName }
    closedAt
    closedBy { uuid email givenName familyName }
  }
`;

/**
 * AdminMeterTradeRulesItem functional component
 * @param {object} props - Component props
 * @returns {React.ReactElement} - AdminMeterTradeRulesItem component
 */
function AdminMeterTradeRulesItem(props) {
  const {
    propertyId, tradePointId,
  } = props;
  let { rule } = props;
  rule = useFragment(AdminMeterTradeRulesItemFragment, rule);
  const intl = useIntl();

  if (!props) {
    return <Loading />;
  }

  const {
    uuid, tradeType, state, start, finish,
  } = rule;

  const active = { start, finish };
  const { clauses } = rule;
  const clausesSortedByPrice = (!clauses || !clauses.edges)
    ? [] : rule.clauses.edges.map((el) => (el.node)).sort((a, b) => (b.price - a.price));

  const clauseNodes = getMemberNodes(clauses);
  const showConditions = hasSharedTimeOfUseConditions(clauseNodes);

  const {
    direction, trader, userId, tradePoint,
  } = useTradeRuleCounterParty(rule, tradePointId) || {};
  const userName = displayUserName(intl, trader, userId);

  return (
    <tr key={`trade-rule-${uuid}`}>
      <th>
        <UUID uuid={uuid} url={`/properties/${propertyId}/trade-rules/${uuid}`} />
        <br />
        <BadgeTradeDirection direction={direction} />
        <br />
        <BadgeTradeType type={tradeType} />
        <br />
        <BadgeTradeRuleState state={state} />
      </th>
      <td>
        {(trader || userName) && (
          <>
            <strong>{userName}</strong>
            <br />
            <UUID uuid={trader?.uuid ?? userId} />
          </>
        )}
      </td>
      <td>
        {tradePoint && (
          <>
            <strong>{tradePoint.label}</strong>
            <br />
            <UUID uuid={tradePoint.tradePoint.uuid} />
            <br />
            <BadgeTradePointType type={tradePoint.tradePoint.type} />
          </>
        )}
      </td>
      <td>
        <ul className="ps-2">
          {clausesSortedByPrice?.map((clause) => (
            <AdminMeterTradeRulesTableItemClause
              clause={clause}
              key={clause.uuid}
              showConditions={showConditions}
            />
          ))}
        </ul>
      </td>
      <td>
        {buildTimeRangeSummary(intl, active)}
      </td>
    </tr>
  );
}

AdminMeterTradeRulesItem.propTypes = {
  tradePointId: PropTypes.string.isRequired,
  propertyId: PropTypes.string.isRequired,
  rule: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default AdminMeterTradeRulesItem;
